import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Users, UserPlus, Library, Calendar, ChevronRight, Copy, Check, TrendingUp, MessageSquare, ShoppingBasket } from 'lucide-react';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoachDashboardModern({ user, onLogout }) {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    subscription_plan: 'Standard',
    start_date: new Date().toISOString().split('T')[0]
  });
  const [inviteLink, setInviteLink] = useState('');
  const [showInviteLink, setShowInviteLink] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchRecentCheckins();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/coach/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentCheckins = async () => {
    try {
      // Get all clients
      const clientsRes = await axios.get(`${API}/coach/clients`);
      const clients = clientsRes.data || [];
      
      // Get check-ins for all clients from last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const checkinsPromises = clients.map(async (client) => {
        try {
          const checkinsRes = await axios.get(`${API}/checkins`, {
            params: { user_id: client.id }
          });
          
          const clientCheckins = checkinsRes.data || [];
          
          // Filter for recent check-ins (last 7 days) that are NOT reviewed
          const recentClientCheckins = clientCheckins.filter(checkin => {
            const checkinDate = new Date(checkin.created_at || checkin.submitted_at);
            const isRecent = checkinDate >= sevenDaysAgo;
            const isNotReviewed = !checkin.review_status || checkin.review_status !== 'reviewed';
            return isRecent && isNotReviewed;
          });
          
          if (recentClientCheckins.length > 0) {
            // Get the most recent one
            const latest = recentClientCheckins.sort((a, b) => 
              new Date(b.created_at || b.submitted_at) - new Date(a.created_at || a.submitted_at)
            )[0];
            
            return {
              client,
              checkin: latest,
              score: ((latest.consistency || 0) + (latest.hunger || 0) + (latest.energy || 0)) / 3
            };
          }
          return null;
        } catch (error) {
          return null;
        }
      });
      
      const results = await Promise.all(checkinsPromises);
      const validCheckins = results.filter(r => r !== null);
      
      // Sort by most recent first
      validCheckins.sort((a, b) => 
        new Date(b.checkin.created_at || b.checkin.submitted_at) - 
        new Date(a.checkin.created_at || a.checkin.submitted_at)
      );
      
      setRecentCheckins(validCheckins);
    } catch (error) {
      console.error('Failed to fetch recent check-ins:', error);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API}/coach/invite-client`, {
        ...formData,
        start_date: new Date(formData.start_date).toISOString()
      });
      
      const link = `${window.location.origin}/signup/${response.data.invitation_token}`;
      setInviteLink(link);
      setShowInviteLink(true);
      toast.success('Invitation created!');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink).then(() => {
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const closeInviteDialog = () => {
    setInviteOpen(false);
    setShowInviteLink(false);
    setInviteLink('');
    setCopied(false);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      subscription_plan: 'Standard',
      start_date: new Date().toISOString().split('T')[0]
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a61e8] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {user.name || 'Coach'}! 👋
          </h1>
          <p className="text-gray-600">Here's your coaching overview</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Users className="h-4 w-4" />
                Total Clients
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900">{dashboard?.total_clients || 0}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Active Plans
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900">{dashboard?.active_meal_plans || 0}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Pending Check-ins
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900">{dashboard?.pending_checkins || 0}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Library className="h-4 w-4" />
                Recipe Library
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900">{dashboard?.total_recipes || 0}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <ShoppingBasket className="h-4 w-4" />
                Food Bank
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-900">{dashboard?.total_food_items || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Check-ins - PENDING REVIEWS */}
        {recentCheckins.length > 0 && (
          <Card 
            className="mb-8 border-l-4 border-l-blue-500 cursor-pointer hover:shadow-lg transition-all"
            onClick={() => navigate('/coach/weekly-reviews')}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">
                      {recentCheckins.length} Client{recentCheckins.length > 1 ? 's' : ''} Checked In
                    </CardTitle>
                    <p className="text-sm text-gray-600 mt-1">
                      Click to view all clients →
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge className="bg-blue-600 text-white px-3 py-1">
                    Pending Review
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="flex -space-x-2">
                  {recentCheckins.slice(0, 3).map(({ client }) => (
                    <div 
                      key={client.id}
                      className="w-8 h-8 bg-blue-100 border-2 border-white rounded-full flex items-center justify-center"
                    >
                      <span className="text-blue-600 font-semibold text-xs">
                        {client.first_name?.[0]}{client.last_name?.[0]}
                      </span>
                    </div>
                  ))}
                  {recentCheckins.length > 3 && (
                    <div className="w-8 h-8 bg-gray-200 border-2 border-white rounded-full flex items-center justify-center">
                      <span className="text-gray-600 font-semibold text-xs">
                        +{recentCheckins.length - 3}
                      </span>
                    </div>
                  )}
                </div>
                <span className="ml-2">
                  {recentCheckins.slice(0, 3).map(({ client }) => client.first_name).join(', ')}
                  {recentCheckins.length > 3 && ` and ${recentCheckins.length - 3} more`}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-all hover:scale-105"
            onClick={() => navigate('/coach/clients')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="w-12 h-12 bg-[#1a61e8]/10 rounded-lg flex items-center justify-center mb-3">
                    <Users className="h-6 w-6 text-[#1a61e8]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Manage Clients</h3>
                  <p className="text-sm text-gray-600">View and manage all clients</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-all hover:scale-105 border-2 border-green-200"
            onClick={() => navigate('/coach/weekly-reviews')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Weekly Reviews</h3>
                  <p className="text-sm text-gray-600">Bulk approve calorie changes</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-all hover:scale-105"
            onClick={() => navigate('/coach/recipe-library')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="w-12 h-12 bg-[#1a61e8]/10 rounded-lg flex items-center justify-center mb-3">
                    <Library className="h-6 w-6 text-[#1a61e8]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Recipe Library</h3>
                  <p className="text-sm text-gray-600">Batch add & manage recipes</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-lg transition-all hover:scale-105"
            onClick={() => setInviteOpen(true)}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                    <UserPlus className="h-6 w-6 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">Invite Client</h3>
                  <p className="text-sm text-gray-600">Add new client</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Clients */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-[#1a61e8]" />
                Recent Clients
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/coach/clients')}
                className="text-[#1a61e8] hover:text-[#1552c7]"
              >
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {!dashboard?.recent_clients || dashboard.recent_clients.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p>No clients yet. Invite your first client to get started!</p>
                <Button 
                  onClick={() => setInviteOpen(true)}
                  className="mt-4 bg-[#1a61e8] hover:bg-[#1552c7]"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite Client
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {dashboard.recent_clients.slice(0, 5).map((client) => (
                  <div 
                    key={client.id} 
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/coach/client/${client.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#1a61e8]/10 rounded-full flex items-center justify-center">
                        <span className="text-[#1a61e8] font-semibold">
                          {client.name?.charAt(0) || client.email?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{client.name || 'No name'}</p>
                        <p className="text-sm text-gray-600">{client.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {client.current_weight && (
                        <span className="text-sm font-medium text-gray-700">
                          {client.current_weight} kg
                        </span>
                      )}
                      {client.has_meal_plan && (
                        <Badge className="bg-[#1a61e8]/10 text-[#1a61e8]">Active Plan</Badge>
                      )}
                      {client.checkin_status === 'pending' && (
                        <Badge className="bg-orange-100 text-orange-700">Check-in Pending</Badge>
                      )}
                      {client.checkin_status === 'completed' && (
                        <Badge className="bg-blue-100 text-blue-700">Checked In</Badge>
                      )}
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={closeInviteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite New Client</DialogTitle>
            <DialogDescription>
              Create an invitation link for your client
            </DialogDescription>
          </DialogHeader>

          {!showInviteLink ? (
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="subscription_plan">Subscription Plan</Label>
                <Select
                  value={formData.subscription_plan}
                  onValueChange={(value) => setFormData({ ...formData, subscription_plan: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Standard">Standard</SelectItem>
                    <SelectItem value="Premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>

              <Button type="submit" className="w-full bg-[#1a61e8] hover:bg-[#1552c7]">
                Generate Invitation Link
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Share this link with your client:</p>
                <code className="text-sm break-all bg-white p-2 rounded block">
                  {inviteLink}
                </code>
              </div>

              <Button
                onClick={copyInviteLink}
                className="w-full bg-[#1a61e8] hover:bg-[#1552c7]"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Link
                  </>
                )}
              </Button>

              <Button
                onClick={closeInviteDialog}
                variant="outline"
                className="w-full"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
